import axios, { AxiosRequestConfig, CancelToken } from 'axios';
import { TokenRefreshedInterceptor } from '@/api/interceptors/token-refreshed-interceptor';
import { Forbidden } from '@/models/forbidden-error';
import { NotFound } from '@/models/not-found-error';
import store from '@/stores/store';
import { Customer } from '@/models/customer';
import { Site } from '@/models/site';

export abstract class Api {
    protected async getAsync<T>(url: string, data?: any, cancellationToken: CancelToken | null = null): Promise<T> {
        url = this.buildUrl(url);

        try {
            const response = await axios.get(url, this.buildConfig(cancellationToken, data));

            this.throwIfRequestWasUnsuccessful(url, response);

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }
    protected async getFileAsync<T>(url: string, data?: any, cancellationToken: CancelToken | null = null): Promise<T> {
        url = this.buildUrl(url);

        try {
            const config = this.buildConfig(cancellationToken, data);
            config.responseType = 'blob';

            const response = await axios.get(url, config);

            this.throwIfRequestWasUnsuccessful(url, response);

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }

    protected async getFileFromPostAsync<T>(url: string, data?: any, cancellationToken: CancelToken | null = null): Promise<T> {
        url = this.buildUrl(url);

        try {
            const config = this.buildConfig(cancellationToken);
            config.responseType = 'blob';

            const response = await axios.post(url, data, config);

            this.throwIfRequestWasUnsuccessful(url, response);

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }

    protected async postAsync<T>(url: string, data: any, cancellationToken: CancelToken | null = null): Promise<T> {
        url = this.buildUrl(url);

        try {
            const response = await axios.post(url, data, this.buildConfig(cancellationToken));

            this.throwIfRequestWasUnsuccessful(url, response);

            if (response.headers.location) {
                return response.headers.location as T;
            }

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }
    protected async postFileAsync<T>(url: string, data: File, cancellationToken: CancelToken | null = null): Promise<T> {
        url = this.buildUrl(url);

        try {
            const formData: FormData = new FormData();
            formData.append('file', data);

            const config = this.buildConfig(cancellationToken);
            config.headers['Content-type'] = 'multipart/form-data';

            const response = await axios.post(url, formData, this.buildConfig(cancellationToken));

            if (response.headers.location) {
                return response.headers.location as T;
            }

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }
    protected async putAsync<T>(url: string, data: any, cancellationToken: CancelToken | null = null) {
        url = this.buildUrl(url);

        try {
            const response = await axios.put(url, data, this.buildConfig(cancellationToken));

            this.throwIfRequestWasUnsuccessful(url, response);

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }
    protected async patchAsync<T>(url: string, data: any, cancellationToken: CancelToken | null = null) {
        url = this.buildUrl(url);

        try {
            const response = await axios.patch(url, data, this.buildConfig(cancellationToken));

            this.throwIfRequestWasUnsuccessful(url, response);

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }
    protected async deleteAsync<T>(url: string, cancellationToken: CancelToken | null = null) {
        url = this.buildUrl(url);

        try {
            const response = await axios.delete(url, this.buildConfig(cancellationToken));

            this.throwIfRequestWasUnsuccessful(url, response);

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }
    protected buildUrl(url: string): string {
        const location = window.location;

        if (location.hostname === 'localhost') {
            return `${location.protocol}//localhost:58575/${url}`;
        }

        return `${location.protocol}//api.${location.host}/${url}`;
    }
    protected buildConfig(cancellationToken: CancelToken | null, data?: any): AxiosRequestConfig {
        const config: AxiosRequestConfig = {
            cancelToken: cancellationToken || undefined,
            headers: {
                'Accept': 'application/json',
                'Content-type': 'application/json',
            },
            params: data || undefined,
            validateStatus: (status: number) => {
                return status >= 200 && status < 300;
            },
            withCredentials: true,
        };

        if (store.getters['user/currentCustomer'] !== null) {
            const customer = store.getters['user/currentCustomer'] as Customer;
            config.headers['X-Customer-Id'] = customer.id;
        }

        if (store.getters['user/currentSite'] !== null) {
            const site = store.getters['user/currentSite'] as Site;
            config.headers['X-Site-Id'] = site.id;
        }

        return config;
    }
    private throwIfRequestWasUnsuccessful(url: string, response: any) {
        if (response && response.response && (response.response.status < 200 || response.response.status >= 300)) {
            if (response.response.status === 403) {
                throw new Forbidden(`Request to ${url} failed with a response status of: ${response.response.status}.`);
            }
            if (response.response.status === 404) {
                throw new NotFound(`Request to ${url} failed with a response status of: ${response.response.status}.`);
            }
            throw new Error(`Request to ${url} failed with a response status of: ${response.response.status}.`);
        }
    }
    public constructor() {
        axios.interceptors.response.use(undefined, TokenRefreshedInterceptor);
    }
}
